import React, { useState, useCallback } from "react";
import { makeStyles } from "@mui/styles";
import { MailProDialog } from "views/Pma/MailPro/components/MailProDialog/MailProDialog";
import { MailProDialogHeader } from "views/Pma/MailPro/components/MailProDialog/MailProDialogHeader";
import { MailProDialogBody } from "views/Pma/MailPro/components/MailProDialog/MailProDialogBody";
import MailProButton from "views/Pma/MailPro/components/UI/MailProButton";
import { RED_PRIMARY, DIALOG_BUTTON_BORDER_RADIUS, DIALOG_BUTTON_MIN_HEIGHT } from "./constants";
import MiniTableProCore from "./MiniTableProCore";

const useStyles = makeStyles({
  dialogPaper: {
    // +10% vs prior 1180px — room for property bar labels (e.g. "You EDITED This Property")
    maxWidth: "1298px",
    width: "99vw",
    overflow: "visible",
    "& .MuiDialogContent-root": {
      padding: "0 !important",
      overflow: "visible !important",
    },
  },
  dialogHeader: {
    backgroundColor: "#4A7396",
    color: "white",
    padding: "8px 24px",
    textAlign: "center",
    width: "100%",
  },
  headerTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "center",
  },
  headerSubtitle: {
    fontSize: "16px",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "center",
    opacity: 0.95,
  },
  dialogBody: {
    padding: "24px 100px 24px 24px",
    maxHeight: "70vh",
    overflowY: "auto",
    overflowX: "visible",
  },
  tableWrap: {
    // −5% vs prior 1080px
    maxWidth: "1026px",
    width: "100%",
    margin: "0 auto",
    overflow: "visible",
  },
  dialogFooter: {
    padding: "16px 24px",
    background: "#f9fafb",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    alignItems: "stretch",
  },
  dialogFooterActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    width: "100%",
  },
  dialogFooterActionsLeft: {
    flexShrink: 0,
  },
  dialogFooterActionsCenter: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
  },
  dialogFooterActionsRight: {
    flexShrink: 0,
  },
  dialogFooterCount: {
    fontSize: "14px",
    fontWeight: 600,
    color: RED_PRIMARY,
    fontFamily: "objektiv-mk2, sans-serif",
  },
  "@keyframes footerWarningBlink": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.45 },
  },
  dialogFooterWarning: {
    fontSize: "11px",
    fontWeight: "bold",
    color: RED_PRIMARY,
    fontFamily: "objektiv-mk2, sans-serif",
    lineHeight: 1.1,
    animation: "$footerWarningBlink 2s ease-in-out infinite",
    "& strong": { color: RED_PRIMARY },
  },
});

/**
 * Dialog for selecting and deleting properties from the mini table.
 * Opens when user clicks "DELETE a Property" in EditTableButtonsDialog.
 * Owns selection state and renders both the deletable table and the footer.
 */
export default function DeletePropertyDialog({
  open,
  onClose,
  areaName = "",
  properties = [],
  onDelete,
  maxRows,
  currentArea,
  quarterlyAreaTable,
  agentData,
}) {
  const classes = useStyles();
  const [selectedIndices, setSelectedIndices] = useState(new Set());

  const handleCheckboxChange = useCallback((index) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const handleDelete = useCallback(() => {
    if (selectedIndices.size === 0) return;
    const indicesArray = Array.from(selectedIndices).sort((a, b) => a - b);
    onDelete?.(indicesArray);
    onClose?.();
    setSelectedIndices(new Set());
  }, [selectedIndices, onDelete, onClose]);

  const handleCancel = useCallback(() => {
    setSelectedIndices(new Set());
    onClose?.();
  }, [onClose]);

  const selectedCount = selectedIndices.size;

  return (
    <MailProDialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }}>
      <MailProDialogHeader>
        <div className={classes.dialogHeader}>
          <div className={classes.headerTitle}>{areaName}</div>
          <div className={classes.headerSubtitle}>Select any property to DELETE</div>
        </div>
      </MailProDialogHeader>
      <MailProDialogBody>
        <div
          className={`${classes.dialogBody} mini-table-dialog-body delete-property-dialog-body`}
        >
          <div className={classes.tableWrap}>
            <MiniTableProCore
              miniTableProperties={properties}
              printVersion={false}
              deletable={true}
              selectedIndices={selectedIndices}
              onCheckboxChange={handleCheckboxChange}
              hideTitleAndSubtitle={true}
              maxRows={maxRows}
              currentArea={currentArea}
              quarterlyAreaTable={quarterlyAreaTable}
              agentData={agentData}
            />
          </div>
        </div>
      </MailProDialogBody>
      <div className={classes.dialogFooter}>
        {selectedCount > 0 && (
          <div className={classes.dialogFooterWarning}>
            <strong>PLEASE NOTE:</strong> You are deleting {selectedCount} propert{selectedCount === 1 ? "y" : "ies"} from the table. (If you change your mind, you can add deleted properties back.)
          </div>
        )}
        <div className={classes.dialogFooterActions}>
          <div className={classes.dialogFooterActionsLeft}>
            <MailProButton
              variant="outlined"
              label="Cancel"
              onClick={handleCancel}
              customStyle={{
                fontSize: "12px",
                padding: "6px 14px",
                textTransform: "uppercase",
                fontWeight: "bold",
                minWidth: "auto",
                borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
                minHeight: DIALOG_BUTTON_MIN_HEIGHT,
                height: DIALOG_BUTTON_MIN_HEIGHT,
                boxSizing: "border-box",
              }}
            />
          </div>
          <div className={classes.dialogFooterActionsCenter}>
            <MailProButton
              variant="filled"
              label="Update"
              onClick={handleDelete}
              disabled={selectedCount === 0}
              customStyle={{
                fontSize: "12px",
                padding: "6px 14px",
                textTransform: "uppercase",
                fontWeight: "bold",
                minWidth: "auto",
                borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
                minHeight: DIALOG_BUTTON_MIN_HEIGHT,
                height: DIALOG_BUTTON_MIN_HEIGHT,
                boxSizing: "border-box",
                backgroundColor: selectedCount === 0 ? "#9ca3af" : RED_PRIMARY,
                borderColor: selectedCount === 0 ? "#9ca3af" : RED_PRIMARY,
              }}
            />
          </div>
          <div className={classes.dialogFooterActionsRight}>
            <span className={classes.dialogFooterCount}>
              {selectedCount} Propert{selectedCount === 1 ? "y" : "ies"} Selected
            </span>
          </div>
        </div>
      </div>
    </MailProDialog>
  );
}
